import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useRequest } from '@common/services/api';
import { useAppSelector } from '@common/hooks/redux';
import { selected } from '@modules/organisation/selectors/organisation';

import Spinner from '@common/components/spinner';
import Placeholder from '@common/components/placeholder';
import OrganizationGroupCard from './organization-group-card';
import { EOrganizationGroupAccessRequestStatus, EOrganizationGroupClickType, EOrganizationGroupVisibility, OrganizationGroup }
  from '../../types/objects';
import SearchBar from '@common/components/search-bar';
import { useDispatch } from 'react-redux';
import leaveGroup from '../../actions/leave-group';
import requestAccess from '../../actions/request-access';
import revokeAccessRequest from '../../actions/revoke-access-request';

import * as Alert from '@common/services/alert';
import { useCanAccess } from '@common/hooks';
import { EPermissions } from '@common/definitions';

interface Props {
  onSelectChannel: (channel: string) => void;
}

const validVisibilities = [
  EOrganizationGroupVisibility.PUBLIC,
  EOrganizationGroupVisibility.PUBLIC_WITH_APPROVAL
];

enum OrganizationGroupTab {
  All = 'all',
  Public = 'public',
  Pending = 'pending',
}

const OrganizationGroupWidget = ({ onSelectChannel }: Props) => {
  const [search, setSearch] = useState<string | undefined>(undefined);
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<OrganizationGroupTab>(OrganizationGroupTab.All);
  const selectedOrganisation = useAppSelector(selected);
  const { data, loading } = useRequest(`/v3/organisations/${selectedOrganisation.id}/channels`);
  const [organizationGroups, setOrganizationGroups] = useState<OrganizationGroup[]>([]);
  const [loadingGroups, setLoadingGroups] = useState<Record<string, boolean>>({});
  const hasAccess = useCanAccess({ permissions: EPermissions.NETWORK_TEAMS_VIEW });
  useEffect(() => {
    if (data) setOrganizationGroups(data);
  }, [data]);

  const dispatch = useDispatch();

  const renderHeaderContent = () => {
    return (
      <div className="TopNavigationBar__Tabs !tw-mb-[-16px]">
        <div
          className={`TopNavigationBar__Tabs__Item 
            ${activeTab === OrganizationGroupTab.All ? 'TopNavigationBar__Tabs__Item--active' : ''} tw-cursor-pointer`}
          onClick={() => setActiveTab(OrganizationGroupTab.All)}
        >
          {t('organisation:all_groups')}
        </div>
        <div
          className={`TopNavigationBar__Tabs__Item 
            ${activeTab === OrganizationGroupTab.Public ? 'TopNavigationBar__Tabs__Item--active' : ''} tw-cursor-pointer`}
          onClick={() => setActiveTab(OrganizationGroupTab.Public)}
        >
          {t('organisation:public')}
        </div>
        <div
          className={`TopNavigationBar__Tabs__Item 
            ${activeTab === OrganizationGroupTab.Pending ? 'TopNavigationBar__Tabs__Item--active' : ''} tw-cursor-pointer`}
          onClick={() => setActiveTab(OrganizationGroupTab.Pending)}
        >
          {t('organisation:pending_groups')}
        </div>
      </div>
    );
  };

  const activeData: OrganizationGroup[] = useMemo(() => {
    const filtered = activeTab === OrganizationGroupTab.Public ?
      organizationGroups.filter(
        (organizationGroup) => validVisibilities.includes(organizationGroup.visibility))
      : activeTab === OrganizationGroupTab.Pending ?
        organizationGroups.filter(
          (organizationGroup) => organizationGroup.access_request_status === EOrganizationGroupAccessRequestStatus.PENDING)
        : organizationGroups;
    const filteredBySearch = filtered.filter(
      (organizationGroup) => organizationGroup.name.toLowerCase().includes(search?.toLowerCase() || ''));
    return filteredBySearch.sort((a, b) => a.name.localeCompare(b.name));
  }, [organizationGroups, activeTab, search]);

  const handleClickGroup = useCallback(async (group: OrganizationGroup, type: EOrganizationGroupClickType) => {
    setLoadingGroups((prev) => ({ ...prev, [group.id]: true }));
    try {
      if (type === EOrganizationGroupClickType.LEAVE) {
        await dispatch(leaveGroup(selectedOrganisation.id, { ...group, is_member: false, access_request_status: null }));
        setOrganizationGroups((prev) => prev.map((g) => (g.id === group.id
          ? { ...g, access_request_status: null, is_member: false }
          : g)
        )
        );
        Alert.success(t('organisation:success_leaving_group'));
      } else if (type === EOrganizationGroupClickType.REVOKE) {
        await dispatch(revokeAccessRequest(selectedOrganisation.id, group.id));
        setOrganizationGroups((prev) => prev.map((g) => (g.id === group.id
          ? { ...g, access_request_status: null }
          : g)
        )
        );
        Alert.success(t('organisation:success_revoking_access'));
      } else if (type === EOrganizationGroupClickType.JOIN) {
        const res = await dispatch(requestAccess(selectedOrganisation.id, group.id)) as any;
        setOrganizationGroups((prev) => prev.map((g) => (g.id === group.id
          ? {
            ...g,
            access_request_status: group.visibility === EOrganizationGroupVisibility.PUBLIC_WITH_APPROVAL ?
              res?.status : null,
            is_member: group.visibility === EOrganizationGroupVisibility.PUBLIC_WITH_APPROVAL ?
              res?.status === EOrganizationGroupAccessRequestStatus.APPROVED : true,
          }
          : g)
        )
        );
        Alert.success(group.visibility === EOrganizationGroupVisibility.PUBLIC_WITH_APPROVAL ?
          t('organisation:success_joining_group_with_approval') : t('organisation:success_joining_group'));
      }
    } catch (response: any) {
      if (type === EOrganizationGroupClickType.JOIN) {
        Alert.error(t('organisation:error_joining_group'));
      } else if (type === EOrganizationGroupClickType.REVOKE) {
        Alert.error(t('organisation:error_revoking_access'));
      } else if (type === EOrganizationGroupClickType.LEAVE) {
        Alert.error(t('organisation:error_leaving_group'));
      }
    } finally {
      setLoadingGroups((prev) => ({ ...prev, [group.id]: false }));
    }
  }, [dispatch, selectedOrganisation.id]);


  return (
    <div className="OrganizationGroup tw-h-fit ">
      <div className="tw-bg-white tw-p-4">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
          <h5 className="tw-text-2xl-plus tw-font-bold tw-flex-1 tw-mb-0 tw-normal-case">
            <Trans i18nKey="social:groups_explore" />
          </h5>
          <SearchBar
            className="tw-max-w-[300px] !tw-mb-0"
            debounce={false}
            filter={false}
            onSearch={(searchValue: string | null) => setSearch(searchValue || undefined)}
            value={search}
          />
        </div>
        {renderHeaderContent()}
      </div>
      <div className={`tw-overflow-y-auto tw-sticky tw-top-[92px] 
        tw-h-fit tw-max-h-[calc(100vh-220px)] tw-mt-6 tw-mx-6 no-scrollbar`}
      >
        {loading ? (
          <Spinner centered />
        ) : activeData.length === 0 ? (
          <Placeholder
            title={
              activeTab === OrganizationGroupTab.All
                ? t('organisation:no_upcoming_organization_groups')
                : activeTab === OrganizationGroupTab.Public ?
                  t('organisation:no_upcoming_public_organization_groups')
                  : t('organisation:no_upcoming_pending_organization_groups')
            }
          />
        ) : (
          <div className="tw-grid tw-grid-cols-2 tw-gap-4">
            {activeData.map((organizationGroup) => (
              <OrganizationGroupCard
                key={organizationGroup.id}
                organizationGroup={organizationGroup}
                onSelectChannel={onSelectChannel}
                selectedOrganisation={selectedOrganisation}
                handleClickGroup={handleClickGroup}
                isClickable={
                  organizationGroup.is_member || organizationGroup.visibility === EOrganizationGroupVisibility.PUBLIC || hasAccess
                }
                loading={!!loadingGroups[organizationGroup.id]}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganizationGroupWidget;
